import toast from 'react-hot-toast';
import {
  type CancelPlayRequestResponse,
  getPlayRequestCancelApi,
} from 'constants/payloads/playRequests';
import { useApiGateway } from 'hooks/useApi';
import WarningConfirmModal from './WarningConfirmModal';

interface Props {
  requestId: string | null;
  isOpen: boolean;
  /** Go back / X / backdrop: keeps the request and dismisses the confirmation. */
  onClose: () => void;
  /** Runs after the request is cancelled; the modal is already closed by then. */
  onCancelled: () => void | Promise<unknown>;
  /** Who stops seeing the request: a game's organizer or an availability's player. */
  recipient?: 'organizer' | 'player';
}

/**
 * "Cancel request?" confirmation (Figma "Warning"), shared by every surface that
 * shows the viewer's own pending request. Owns the cancel call so callers only
 * decide what to refresh or where to go afterwards.
 */
export default function CancelRequestModal({
  requestId,
  isOpen,
  onClose,
  onCancelled,
  recipient = 'organizer',
}: Props) {
  const { post: cancelRequest, isLoading: isCancelling } = useApiGateway<
    undefined,
    CancelPlayRequestResponse
  >();

  const handleConfirm = async () => {
    if (!requestId) return;
    const response = await cancelRequest({ endpoint: getPlayRequestCancelApi(requestId) });
    if (response.isError) {
      toast.error(response.error || 'Could not cancel your request');
      return;
    }
    onClose();
    await onCancelled();
  };

  return (
    <WarningConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel request?"
      description={`If you cancel, the ${recipient} will no longer see your request.`}
      confirmLabel="Cancel"
      onConfirm={handleConfirm}
      isConfirming={isCancelling}
    />
  );
}
